'use client'

import { useState, useEffect } from 'react'
import { cn } from '@/lib/utils'
import { ProductFullSearchData } from '@/types/product.type'

interface LazyProductGridProps {
  products: ProductFullSearchData[]
  isGrid: boolean
  ProductCard: React.ComponentType<{ product: ProductFullSearchData }>
  ProductCardList: React.ComponentType<{ product: ProductFullSearchData }>
}

export default function LazyProductGrid({ products, isGrid, ProductCard, ProductCardList }: LazyProductGridProps) {
  const [mounted, setMounted] = useState(false)
  const [visibleProducts, setVisibleProducts] = useState<ProductFullSearchData[]>([])

  useEffect(() => {
    setMounted(true)
    setVisibleProducts(products)
  }, [products])

  if (!mounted) {
    return null // Avoid hydration mismatch
  }

  return (
    <div
      className={cn('hidden lg:grid', {
        'grid-cols-4 gap-x-[10px] gap-y-[22px]': isGrid,
        'grid-cols-1 gap-y-6': !isGrid,
      })}
    >
      {visibleProducts
        .map((product, index) => {
          if (!product || !product.id) return null

          if (isGrid) {
            return <ProductCard key={`client-product-card-${product.id}-${index}`} product={product} />
          }

          return (
            <div key={`client-product-${product.id}-${index}`} className="mb-2 pb-6 border-b border-bordercolor">
              <ProductCardList product={product} />
            </div>
          )
        })
        .filter(Boolean)}
      {/* Loading indicator for remaining products */}
      {visibleProducts.length < products.length && (
        <div className="col-span-full flex justify-center p-4">
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:0s]"></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:0.1s]"></div>
            <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:0.2s]"></div>
          </div>
        </div>
      )}
    </div>
  )
}
